import { defineStore } from 'pinia'
import { useMenuStore } from './menu'

// 경로 → 탭 제목 (접두사 매칭). 상세/하위 경로는 접두사로 잡힘.
const TITLE = [
  ['/finance/dashboard', '자금 대시보드'],
  ['/finance/daily-reports', '자금일보'],
  ['/finance/payment-plan', '자금계획'],
  ['/finance/manual-payments', '수동 지급 입력'],
  ['/finance/payment-execution', '지급 집행'],
  ['/finance/receivable-notes', '받을어음'],
  ['/finance/settings', '자금 설정'],
  ['/expenses_manage', '일별지출통계'],
  ['/expenses', '지출결의서'],
  ['/condolences', '부조금관리'],
  ['/leave/admin', '휴가 관리'],
  ['/leave/request', '휴가 신청'],
  ['/leave/approval', '휴가 승인'],
  ['/leave/calender', '휴가 캘린더'],
  ['/brand-assignments', '브랜드 배정'],
  ['/approval/inbox', '결재함'],
  ['/ai-assistant', 'AI 비서'],
  ['/design', '디자인 관리'],
  ['/sample-requests', '샘플요청'],
  ['/quotations', '견적'],
  ['/production-dashboard', '작업현황'],
  ['/main-requests', '메인오더'],
  ['/inspections', '검품 관리'],
  ['/material-dashboard', '자재현황'],
  ['/material-orders', '발주서'],
  ['/receivings', '발주 입고'],
  ['/inbounds', '창고 입고'],
  ['/material-transfers', '자재전달'],
  ['/test-reports', '시험성적서'],
  ['/material-lots', 'LOT 자재'],
  ['/outbounds', '출고관리'],
  ['/packing-lists', '패킹리스트'],
  ['/deliveries', '납품/반품'],
  ['/shipments', '선적 스케줄'],
  ['/lcs', 'LC 관리'],
  ['/lgs', 'LG 관리'],
  ['/import-invoices', '수입송장'],
  ['/lc-repayment-schedule', 'LC 상환 스케줄'],
  ['/freight-costs', '운임비용'],
  ['/tt-payments', 'TT 송금'],
  ['/debit-notes', '데빗노트'],
  ['/cost-comparison', '원가 비교'],
  ['/executive-dashboard', '경영 대시보드'],
  ['/daily-labors', '일용직(노무비)'],
  ['/profile', '내 정보'],
  ['/', '내 업무'],
]

// 경로 끝 숫자 ID → '#123' — 부제 미등록 상세 탭의 기본 구분자
function autoSub(path) {
  const m = /\/(\d+)\/?$/.exec(path || '')
  return m ? `#${m[1]}` : ''
}

function titleOf(path) {
  const exact = TITLE.find(([p]) => p === path)
  if (exact) return exact[1]
  let best = null
  for (const [p, t] of TITLE) {
    if (p !== '/' && path.startsWith(p) && (!best || p.length > best[0].length)) best = [p, t]
  }
  return best ? best[1] : '화면'
}

export const useTabsStore = defineStore('tabs', {
  state: () => ({
    tabs: [], // [{ key: fullPath, title, subtitle }]
    // 상세 화면이 데이터를 로드한 뒤 등록하는 부제 (문서번호·지시번호 등).
    // 탭 목록과 별도 맵으로 관리 — 탭이 닫혔다 다시 열려도 유지됨. (2026-08-01)
    subtitles: {}, // { [fullPath]: '지출-2026-0123' }
  }),
  getters: {
    // 탭 제목 라이브 해석: 메뉴 라벨(서버 단일 소스) → 로컬 특수표 → '화면'
    // 메뉴가 늦게 로드돼도 게터라 자동 갱신됨.
    titleFor: (state) => (key) => {
      const path = (key || '').split('?')[0]
      const menu = useMenuStore()
      const base = menu.titleFor(path) || titleOf(path)
      // 부제: ① 상세 화면이 setSubtitle로 등록한 값(문서번호 등) 우선
      //       ② 없으면 경로 끝 숫자 ID로 임시 구분 (예: /expenses/123 → #123)
      const sub = state.subtitles[key] || autoSub(path)
      return sub ? `${base} · ${sub}` : base
    },
  },
  actions: {
    add(route) {
      const key = route.fullPath
      if (!key || key === '/login') return
      if (!this.tabs.find((t) => t.key === key)) {
        // 탭 상한 10 — App.vue keep-alive :max="10"과 정합. 초과 시 가장
        // 오래된(맨 앞) 탭부터 자동 정리 (11개째부터 상태 보존이 조용히
        // 깨지는 것 방지 + 탭바 오버플로 방지, 2026-08-01)
        while (this.tabs.length >= 10) this.tabs.shift()
        this.tabs.push({ key, title: titleOf(route.path) })
      }
    },
    // 상세 화면에서 데이터 로드 후 호출: 탭 제목 뒤에 '· 부제'로 표시됨
    setSubtitle(key, subtitle) {
      if (!key) return
      if (subtitle) this.subtitles[key] = String(subtitle)
      else delete this.subtitles[key]
    },
    remove(key) {
      const i = this.tabs.findIndex((t) => t.key === key)
      if (i >= 0) this.tabs.splice(i, 1)
      return i
    },
    clearOthers(key) {
      this.tabs = this.tabs.filter((t) => t.key === key)
    },
    reset() {
      this.tabs = []
      this.subtitles = {}
    },
  },
})
